// Deterministic, memoized mapping from a tag name to an accessible color.
//
// Tags carry no color in the data model, so the planner assigns one on the
// frontend: a stable hash of the tag *name* into a fixed, contrast-checked
// palette. Keying by name (not id) means the same tag always resolves to the
// same color everywhere it appears — so an unscheduled stop and the route that
// carries it show the identical hue, and matching becomes a glance.
//
// Each entry is a light tint background with a dark, AA-contrast text color, a
// matching border and a saturated dot. Used by RouteTagChip.

export const TAG_COLORS = [
  { bg: "#E7E9FF", text: "#2A34C4", border: "#CDD2FF", dot: "#3B47E5" }, // indigo
  { bg: "#D6F0EB", text: "#0A7360", border: "#B4E4DB", dot: "#0FA592" }, // teal
  { bg: "#FBEED4", text: "#875311", border: "#F0DBA8", dot: "#C98A16" }, // amber
  { bg: "#FBE0E8", text: "#AC2555", border: "#F3C3D2", dot: "#DA477E" }, // rose
  { bg: "#EEE4FB", text: "#6631A8", border: "#DCC9F3", dot: "#8A54D6" }, // violet
  { bg: "#E4F2DD", text: "#3A7124", border: "#CBE7BE", dot: "#5AA33C" }, // green
  { bg: "#DEECFB", text: "#155FAB", border: "#BBD8F2", dot: "#2B85E0" }, // sky
  { bg: "#FCE7D6", text: "#984812", border: "#F4D0B0", dot: "#E07A2C" }, // orange
  { bg: "#D6F0F4", text: "#0B6472", border: "#B2E2EA", dot: "#1596AB" }, // cyan
  { bg: "#FBE1F3", text: "#9C2A78", border: "#F2C4E4", dot: "#CE4CA3" }, // pink
  { bg: "#E4E8EF", text: "#38465C", border: "#CFD6E1", dot: "#5A6B82" }, // slate
  { bg: "#FBE0DE", text: "#A32820", border: "#F3C5C1", dot: "#D6392E" }, // red
  { bg: "#E6E7FA", text: "#3A32B0", border: "#CBCDF0", dot: "#574DD4" }, // periwinkle
  { bg: "#EAF1CE", text: "#566A11", border: "#DBE7A8", dot: "#86991F" } // lime
];

const cache = new Map();

// Classic 32-bit string hash (matches the deterministic-but-cheap style used
// elsewhere in the app). Deterministic across sessions.
const hashName = name => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash |= 0; // force int32
  }
  return Math.abs(hash);
};

// Returns a stable { bg, text, border, dot } for a tag name.
export const getTagColor = name => {
  const key = (name || "").trim();
  if (!key) return TAG_COLORS[TAG_COLORS.length - 1]; // neutral-ish fallback
  const cached = cache.get(key);
  if (cached) return cached;
  const color = TAG_COLORS[hashName(key) % TAG_COLORS.length];
  cache.set(key, color);
  return color;
};
